/**
 * GalleryProvider
 * Провайдер для работы с галереей изображений книги
 */

import { createContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { GalleryItem, CreateGalleryItemInput, GalleryError, GalleryImageGeneration } from '@/types/gallery';
import { useBooks } from '@/books/useBooks';
import { useAuth } from '@/auth/useAuth';

interface GalleryContextType {
  items: GalleryItem[];
  galleryLoading: boolean;
  galleryError: GalleryError | null;
  uploadProgress: number | null;
  uploadItem: (input: CreateGalleryItemInput) => Promise<GalleryItem | null>;
  upsertLocalItem: (item: GalleryItem) => void;
  deleteItem: (itemId: string) => Promise<void>;
  getImageUrl: (filePath: string) => string;
  loadGallery: (options?: { force?: boolean }) => Promise<void>;
}

export const GalleryContext = createContext<GalleryContextType | undefined>(undefined);

interface GalleryProviderProps {
  children: ReactNode;
}

export function GalleryProvider({ children }: GalleryProviderProps) {
  const { currentBook } = useBooks();
  const { user } = useAuth();
  const currentBookId = currentBook?.id ?? null;
  const userId = user?.id ?? null;
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState<GalleryError | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const loadedGalleryKeyRef = useRef<string | null>(null);
  const inFlightGalleryKeyRef = useRef<string | null>(null);

  // Загрузка элементов галереи текущей книги
  const loadGallery = useCallback(async (options?: { force?: boolean }) => {
    if (!currentBookId || !userId) {
      loadedGalleryKeyRef.current = null;
      inFlightGalleryKeyRef.current = null;
      setItems([]);
      return;
    }

    const galleryKey = `${userId}:${currentBookId}`;
    const forceReload = Boolean(options?.force);

    if (!forceReload && loadedGalleryKeyRef.current === galleryKey) {
      return;
    }

    if (!forceReload && inFlightGalleryKeyRef.current === galleryKey) {
      return;
    }

    inFlightGalleryKeyRef.current = galleryKey;
    setGalleryLoading(true);
    setGalleryError(null);

    try {
      const { data, error } = await supabase
        .from('gallery_items')
        .select('*')
        .eq('book_id', currentBookId)
        .eq('user_id', userId)
        .eq('kind', 'gallery')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Gallery load error:', error);
        setGalleryError({ message: 'Не удалось загрузить галерею', code: error.code });
        setItems([]);
      } else {
        const galleryItems = (data || []) as GalleryItem[];
        const galleryItemIds = galleryItems.map((item) => item.id);

        if (galleryItemIds.length === 0) {
          setItems([]);
          loadedGalleryKeyRef.current = galleryKey;
          return;
        }

        const { data: generationData, error: generationError } = await supabase
          .from('image_generations')
          .select('id, gallery_item_id, prompt, model, aspect_ratio, slot_index, status, provider_cost, provider_currency, client_request_id, created_at')
          .in('gallery_item_id', galleryItemIds)
          .order('created_at', { ascending: false });

        if (generationError) {
          console.error('Gallery generation load error:', generationError);
          setItems(galleryItems);
          loadedGalleryKeyRef.current = galleryKey;
          return;
        }

        const generationsByGalleryItemId = new Map<string, GalleryImageGeneration>();
        ((generationData || []) as GalleryImageGeneration[]).forEach((generation) => {
          if (!generation.gallery_item_id) {
            return;
          }

          const currentGeneration = generationsByGalleryItemId.get(generation.gallery_item_id);
          const hasPrompt = Boolean(generation.prompt?.trim());
          const currentHasPrompt = Boolean(currentGeneration?.prompt?.trim());

          if (!currentGeneration || (!currentHasPrompt && hasPrompt)) {
            generationsByGalleryItemId.set(generation.gallery_item_id, generation);
          }
        });

        setItems(galleryItems.map((item) => {
          const imageGeneration = generationsByGalleryItemId.get(item.id) ?? null;

          return {
            ...item,
            image_generation: imageGeneration,
            prompt: imageGeneration?.prompt ?? null,
            model: imageGeneration?.model ?? null,
            aspect_ratio: imageGeneration?.aspect_ratio ?? null,
            slot_index: imageGeneration?.slot_index ?? null,
          };
        }));
        loadedGalleryKeyRef.current = galleryKey;
      }
    } catch (err) {
      console.error('Gallery load exception:', err);
      setGalleryError({ message: 'Произошла ошибка при загрузке галереи' });
      setItems([]);
    } finally {
      if (inFlightGalleryKeyRef.current === galleryKey) {
        inFlightGalleryKeyRef.current = null;
      }
      setGalleryLoading(false);
    }
  }, [currentBookId, userId]);

  // Загрузка изображения
  const uploadItem = async (input: CreateGalleryItemInput): Promise<GalleryItem | null> => {
    if (!currentBook || !user) {
      setGalleryError({ message: 'Книга или пользователь не выбраны' });
      return null;
    }

    setGalleryError(null);
    setUploadProgress(0);

    try {
      // Генерируем уникальное имя файла
      const fileExt = input.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${user.id}/${currentBook.id}/${fileName}`;

      // Загружаем файл в Storage
      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, input.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('File upload error:', uploadError);
        setGalleryError({ message: 'Не удалось загрузить файл', code: uploadError.message });
        setUploadProgress(null);
        return null;
      }

      setUploadProgress(100);

      // Создаем запись в gallery_items
      const newItem = {
        book_id: currentBook.id,
        user_id: user.id,
        title: input.title,
        file_path: filePath,
        file_url: null,
        mime_type: input.file.type,
        size_bytes: input.file.size,
        kind: input.kind || 'gallery',
        created_at: new Date().toISOString(),
      };

      const { data, error: dbError } = await supabase
        .from('gallery_items')
        .insert([newItem])
        .select()
        .single();

      if (dbError) {
        console.error('Gallery item create error:', dbError);
        // Пытаемся удалить загруженный файл
        await supabase.storage.from('gallery').remove([filePath]);
        setGalleryError({ message: 'Не удалось сохранить изображение', code: dbError.code });
        setUploadProgress(null);
        return null;
      }

      setItems(prev => [data, ...prev]);
      if (currentBookId && userId) {
        loadedGalleryKeyRef.current = `${userId}:${currentBookId}`;
      }
      setUploadProgress(null);
      
      return data;
    } catch (err) {
      console.error('Gallery upload exception:', err);
      setGalleryError({ message: 'Произошла ошибка при загрузке изображения' });
      setUploadProgress(null);
      return null;
    }
  };

  const upsertLocalItem = useCallback((item: GalleryItem) => {
    setItems((prev) => {
      const withoutExistingItem = prev.filter((currentItem) => currentItem.id !== item.id);
      return [item, ...withoutExistingItem];
    });

    if (currentBookId && userId) {
      loadedGalleryKeyRef.current = `${userId}:${currentBookId}`;
    }
  }, [currentBookId, userId]);

  // Удаление элемента галереи
  const deleteItem = async (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    try {
      // Удаляем файл из Storage
      const { error: storageError } = await supabase.storage
        .from('gallery')
        .remove([item.file_path]);

      if (storageError) {
        console.error('File delete error:', storageError);
        setGalleryError({ message: 'Не удалось удалить файл', code: storageError.message });
        return;
      }

      // Удаляем запись из gallery_items
      const { error: dbError } = await supabase
        .from('gallery_items')
        .delete()
        .eq('id', itemId);

      if (dbError) {
        console.error('Gallery item delete error:', dbError);
        setGalleryError({ message: 'Не удалось удалить запись', code: dbError.code });
        return;
      }

      setItems(prev => prev.filter(i => i.id !== itemId));
      if (currentBookId && userId) {
        loadedGalleryKeyRef.current = `${userId}:${currentBookId}`;
      }
    } catch (err) {
      console.error('Gallery delete exception:', err);
      setGalleryError({ message: 'Произошла ошибка при удалении изображения' });
    }
  };

  // Получение URL изображения
  const getImageUrl = (filePath: string): string => {
    const { data } = supabase.storage
      .from('gallery')
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  };

  // Загружаем галерею при смене книги
  useEffect(() => {
    if (currentBookId && userId) {
      // Real async side-effect: load the gallery for the active book/user (loadGallery is
      // useCallback([currentBookId, userId])). The loading/error state set synchronously inside
      // loadGallery is inherent to the fetch, not state derivable at render.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async gallery fetch; loading state is inherent to the side-effect
      loadGallery();
    } else {
      loadedGalleryKeyRef.current = null;
      inFlightGalleryKeyRef.current = null;
      setItems([]);
    }
  }, [currentBookId, userId, loadGallery]);

  const value = {
    items,
    galleryLoading,
    galleryError,
    uploadProgress,
    uploadItem,
    upsertLocalItem,
    deleteItem,
    getImageUrl,
    loadGallery,
  };

  return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
}
